import type { UseQueryResult } from "@edreamsodigeo/frontend-api-client";

import useGetSubscriptionOffers from "~/clientState/hooks/useGetSubscriptionOffers";
import type {
  SubscriptionOffersQuery,
  SubscriptionOffersQueryVariables,
  SubscriptionResponse,
} from "~/generated/graphql";
import {
  MembershipPerkType,
  SubscriptionOfferPeriod,
  SubscriptionOfferType,
} from "~/generated/graphql";

export interface GetBenefitFromOffer {
  isBenefitIncluded: boolean;
  offers: Array<SubscriptionResponse>;
  error: UseQueryResult<
    SubscriptionOffersQuery,
    SubscriptionOffersQueryVariables
  >["errors"];
}

export interface UseGetBenefitFromOfferProps {
  offerPeriod?: SubscriptionOfferPeriod;
  offerType?: SubscriptionOfferType;
  perkType?: MembershipPerkType;
}

interface BenefitIncludedProps {
  offers: Array<SubscriptionResponse>;
  perkType: MembershipPerkType;
}

export const isPerkTypeIncluded = ({
  offers,
  perkType,
}: BenefitIncludedProps): boolean => {
  return offers.some((offer) =>
    offer.catalogueOffer?.plan?.membershipTier?.valueProposition?.some(
      (perk) => perk.type === perkType,
    ),
  );
};

const filterOfferByType = ({ offers, type }): Array<SubscriptionResponse> =>
  offers.filter((offer: SubscriptionResponse) => offer.type === type);

const useGetBenefitFromOffer = ({
  offerPeriod = SubscriptionOfferPeriod.FreeTrial,
  offerType = SubscriptionOfferType.Plus,
  perkType = MembershipPerkType.ItineraryCancellationForSubscriptionPurchase,
}: UseGetBenefitFromOfferProps = {}): GetBenefitFromOffer => {
  const { offers: offersList, error } = useGetSubscriptionOffers({
    offerPeriod,
  });

  const filteredOffers = filterOfferByType({
    offers: offersList,
    type: offerType,
  });

  const isBenefitIncluded = isPerkTypeIncluded({
    offers: filteredOffers,
    perkType: perkType,
  });

  return {
    isBenefitIncluded,
    offers: filteredOffers,
    error,
  };
};

export default useGetBenefitFromOffer;
